import {
  useEvent,
  isTask,
  useComments,
  useRequiredConfig,
  useApproval,
  isSpot,
  Spot
} from '@domain/shifthandover'
import {ContentBox} from '@hconnect/common/components/ContentBox'
import {Tag} from '@hconnect/common/components/Tag'
import {MAX_ALLOWED_ATTACHMENTS, SIDE_CARD_MIN_HEIGHT_IN_PX} from '@hconnect/common/consts'
import {headlineSx, imageBoxSx} from '@hconnect/common/styles/images'
import {Stoppage, ShiftEvent} from '@hconnect/common/types'
import {getUniqueAttachments} from '@hconnect/common/utils'
import {CardSectionTitle} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {usePermission} from '@hproduce/permissions'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {Box, Button, CircularProgress, Divider} from '@mui/material'
import {AttachmentItem, AttachmentItemsDisplay} from '@widgets/common-attachments'
import {EventTaskChecklists} from '@widgets/hroc-checklists'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {Comments} from '../components/Comments'
import {EventCreate} from '../components/EventCreate'
import {EventEdit} from '../components/EventEdit'
import {AnomalyMediaViewer, MediaThumb} from '../components/eventProperties/AnomalyMediaViewer'
import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../consts'
import {useSpotAnomalyMedia} from '../hooks/useSpotAnomalyMedia'
import {useTaskChecklists} from '../hooks/useTaskChecklists'
import {EventDetails} from '../layouts/EventDetails'
import {EventNotFound} from '../layouts/EventNotFound'
import {ShiftEventDetailsModes, ShiftEventDetailsWidgetProps} from '../types'

export function getItemId(mode: ShiftEventDetailsModes): string | undefined {
  switch (mode.mode) {
    case 'detailsMode':
    case 'editMode':
      return mode.itemId
    default:
      return undefined
  }
}

// eslint-disable-next-line complexity
export const ShiftEventDetailsWidget: React.FC<ShiftEventDetailsWidgetProps> = (props) => {
  const itemId = getItemId(props)
  const {plantId} = props
  const config = useRequiredConfig(plantId)
  const {country, isEquipmentAvailable} = config
  const timezone = useUpmPlantTimezone(plantId)
  const plantContext = {plantId, countryId: country}
  const allowedToViewDocuments = usePermission({
    plantContext,
    permissionTypes: ['VIEW_DOCUMENTS']
  })
  const [activeAttachmentId, setActiveAttachmentId] = useState<number | undefined>(undefined)
  const [activeMediaIndex, setActiveMediaIndex] = useState<number | null>(null)
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  // FYI useEvent will first try to reuse the cash of the previous eventSearch before fetching the single item
  // as the image urls change on every call, we suppress it in edit mode to not end up with an invalid list of attachments
  const {
    data: item,
    error: errorOnUseEvent,
    isFetching,
    refetch
  } = useEvent(
    {
      plantId,
      timezone,
      eventId: itemId
    },
    {
      refetchOnWindowFocus: props.mode === 'detailsMode',
      enabled: !props.item
    }
  )
  const areChecklistsEnabled = useCommonFeatureFlag(FeatureFlagKey.Checklists, {
    plantId
  })

  const canViewChecklists = usePermission({
    plantContext,
    permissionTypes: ['VIEW_CHECKLISTS']
  })

  const showChecklists = !!areChecklistsEnabled && canViewChecklists

  const {data: taskChecklists = [], isLoading: isLoadingChecklists} = useTaskChecklists({
    plantId,
    taskId: itemId,
    options: {
      enabled: !!itemId && !!item && isTask(item) && showChecklists
    }
  })

  const onSubmit = (next: ShiftEvent) => {
    // safety-net mostly for development
    // 'next' should always include a proper ShiftEvent
    // otherwise it will produce a non-speaking "can not read property 'toString' of undefined" higher in the tree
    if (!next) {
      throw new Error('onSubmit did not include an event object')
    } else if (Array.isArray(next)) {
      throw new Error('onSubmit received an array instead of an event object')
    } else if (!next.id) {
      throw new Error(`onSubmit was not able to extract an id from the given ${typeof next}`)
    }
    props.setMode({mode: 'detailsMode', itemId: next.id})
  }
  const {refetch: refetchComments} = useComments({
    plantId,
    eventId: itemId ?? '',
    timezone,
    options: {enabled: !!itemId}
  })

  const itemToUse = item || props.item
  const spotEvent: Spot | null =
    itemToUse && isSpot(itemToUse as unknown as Partial<Spot>)
      ? (itemToUse as unknown as Spot)
      : null
  const {isLoading: isLoadingMedia, mediaItems, orbitLink} = useSpotAnomalyMedia(spotEvent, plantId)

  if (props.mode === 'createMode') {
    return (
      <EventCreate
        data-test-id="sideCard-create"
        doClose={props.doClose}
        key={'createMode'}
        onSubmit={onSubmit}
        preSetEventType={props.preSetEventType}
        initialData={props.initialData}
        onSuccess={props.onCreationSuccess}
        eventTypeLabelPrefix={props.eventTypeLabelPrefix}
        hideStoppageDateType={props.hideStoppageDateType}
        plantId={plantId}
        checklistsInputSettings={{
          showChecklists,
          disableSelect: false
        }}
        onCreationStart={props.onCreationStart}
        onCreationFinish={props.onCreationFinish}
        skipOverlapValidation={props.skipOverlapValidation}
      />
    )
  }

  if (errorOnUseEvent) {
    return <EventNotFound itemId={props.itemId} doClose={() => props.doClose()} />
  }

  const uniqueAttachments = getUniqueAttachments(itemToUse?.attachments, MAX_ALLOWED_ATTACHMENTS)

  if (itemToUse) {
    switch (props.mode) {
      case 'detailsMode':
        return (
          <EventDetails
            data-test-id="page-events-details"
            actions={{
              doClose: props.doClose,
              doEdit: (next: ShiftEvent) => {
                props.setMode({mode: 'editMode', itemId: next.id})
              },
              doStoppageSplit: (initialStoppageData: Partial<Stoppage>) => {
                props.setMode({
                  mode: 'createMode',
                  initialData: initialStoppageData,
                  skipOverlapValidation: true
                })
              },
              onSapNotificationCreated: () => void refetch()
            }}
            item={itemToUse}
            plantId={plantId}
            key={'detailsMode-' + props.itemId}
            disableSap={props.disableSap}
            isSeriesPreview={props.isSeriesPreview}
            freeSearchText={props.freeSearchText}
            hasChecklists={!!taskChecklists.length}
            refetchComments={() => void refetchComments()}
            onSapPmNotification={props.onSapPmNotification}
            useApprovalHook={useApproval}
          >
            {isTask(itemToUse) && showChecklists && (
              <Box sx={{my: 1.5}}>
                <Tag label={t('shiftEvent.label.safetyChecklists')}>
                  <EventTaskChecklists
                    checklists={taskChecklists}
                    isLoading={isLoadingChecklists}
                    plantId={plantId}
                  />
                </Tag>
              </Box>
            )}
            {allowedToViewDocuments && (uniqueAttachments || spotEvent) ? (
              <>
                <CardSectionTitle sx={headlineSx()}>
                  {t('shiftEvent.attachments')}{' '}
                  {`${(uniqueAttachments?.length ?? 0) + mediaItems.length}/${MAX_ALLOWED_ATTACHMENTS}`}
                </CardSectionTitle>
                <Divider sx={{borderColor: 'divider'}} />
                {orbitLink && (
                  <Box mt={1}>
                    <Button
                      component="a"
                      href={orbitLink.linkTargetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      startIcon={<OpenInNewIcon />}
                      variant="outlined"
                      size="small"
                      data-test-id="spot-media-orbit-link"
                    >
                      {t('shiftEvent.action.openInOrbitPortal')}
                    </Button>
                  </Box>
                )}
                <Box component={'span'} sx={imageBoxSx()}>
                  {!isFetching &&
                    uniqueAttachments?.map((attachment, index) => (
                      <AttachmentItem
                        key={`attachment-${attachment.id}`}
                        attachment={attachment}
                        setActiveAttachmentId={setActiveAttachmentId}
                        disableError={typeof activeAttachmentId === 'number' || !!index}
                      />
                    ))}
                  {isLoadingMedia && <CircularProgress size={24} sx={{ml: 2, mb: 1}} />}
                  {!isLoadingMedia &&
                    mediaItems.map((mediaItem, index) => (
                      <span
                        key={mediaItem.dataCaptureUuid}
                        onClick={() => setActiveMediaIndex(index)}
                      >
                        <MediaThumb mediaItem={mediaItem} />
                      </span>
                    ))}
                </Box>
              </>
            ) : (
              <></>
            )}

            <Comments
              key={props.itemId}
              eventId={props.itemId}
              plantId={plantId}
              country={country}
            />
            {typeof activeAttachmentId === 'number' && (
              <AttachmentItemsDisplay
                attachments={uniqueAttachments}
                activeAttachmentId={activeAttachmentId}
                onClose={() => setActiveAttachmentId(undefined)}
              />
            )}
            {activeMediaIndex !== null && (
              <AnomalyMediaViewer
                mediaItems={mediaItems}
                activeIndex={activeMediaIndex}
                onClose={() => setActiveMediaIndex(null)}
              />
            )}
          </EventDetails>
        )

      case 'editMode':
        return (
          <EventEdit
            data-test-id="sideCard-edit"
            doClose={props.doClose}
            onSubmit={onSubmit}
            item={itemToUse}
            key={'editMode'}
            onlyEditSeries={props.isSeriesPreview}
            plantId={plantId}
            country={country}
            isEquipmentAvailable={isEquipmentAvailable}
            checklistsInputSettings={{
              showChecklists,
              disableSelect: true,
              initialValue: taskChecklists
            }}
          />
        )
    }
  }

  return (
    <ContentBox
      data-test-id="sideCard-spinner"
      isLoading
      minHeight={SIDE_CARD_MIN_HEIGHT_IN_PX}
      mode="max100PercentOfParentHeight"
    />
  )
}
